/**
 * FLAGS de la TITULARIDAD (B1 · `docs/plans/01-titularidad.md`).
 *
 * Seis interruptores, uno por etapa reversible sola. La regla que los ordena:
 * **`academy.entitlement` apagada ⇒ el servidor no bloquea a nadie**. No es un
 * matiz: es el default de fábrica. Encender la vertical no debe cambiar el
 * comportamiento de nadie hasta que un administrador decida una política — un
 * feature de dinero que se activa solo es un incidente.
 *
 * Lo que estos flags NO son: una feature flag es una decisión de producto
 * uniforme por (academia, curso); la titularidad es una decisión **por usuario y
 * por momento**. Por eso la titularidad no vive en `feature_flags` (haría falta
 * una fila por alumno) y por eso el flag `sync_scoping` **no recorta ningún
 * stream**: gatea lo que el materializador escribe. Recortar streams por feature
 * rompería el local-first sin ganar seguridad.
 *
 * `dataPolicy: "preserved"` con un matiz que hay que decir: en el SERVIDOR nunca
 * se borra nada, pero acotar la réplica **sí** retira filas del dispositivo del
 * alumno al reconectar. El texto de `flowsBlocked` lo dice para que el diálogo
 * de confirmación no mienta.
 */

import type { FeatureManifest } from "../country/packs";

const ENTITLEMENT: FeatureManifest = {
  key: "academy.entitlement",
  name: "Titularidad (qué pasa si no ha pagado)",
  description:
    "Decide qué ocurre cuando un alumno no ha pagado, con herencia en cascada: plataforma → academia → programa → curso → cohorte → alumno concreto. Apagada, nadie se bloquea: el servidor calcula y explica, pero no retira nada.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: un feature de dinero que se activa solo es un incidente.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  // Arista BLANDA: la titularidad se apoya en el billing para saber quién debe,
  // pero no queda rehén de él — una academia puede tener la vertical de cobros
  // apagada y seguir queriendo el cálculo apagado y explicable.
  requires: ["billing.core?"],
  implications: {
    navHidden: ["Titularidad (/academy/entitlement)"],
    flowsBlocked: [
      "Aplicar la política de impago (nadie queda bloqueado)",
      "Materializar el estado de titularidad de los alumnos",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_POLICIES: FeatureManifest = {
  key: "academy.entitlement.policies",
  name: "Políticas de titularidad",
  description:
    "Edición de la política por nivel (plataforma, academia, programa, curso, cohorte): días de gracia, umbral mínimo de deuda, ventana para pagos pendientes de verificar y qué se retira. Una política «cerrada» impide que los niveles más específicos la aflojen.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: un feature de dinero que se activa solo es un incidente.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Crear, editar y borrar políticas de titularidad"],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_REST_GATES: FeatureManifest = {
  key: "academy.entitlement.rest_gates",
  name: "Bloqueo de grabaciones, clase en vivo y certificado",
  description:
    "Los tres activos que el servidor sirve frescos y que, por tanto, son REALMENTE revocables. Apagado, la titularidad se calcula y se explica pero no muerde: es el modo «observar antes de aplicar».",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: un feature de dinero que se activa solo es un incidente.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Retener la reproducción de grabaciones a un alumno moroso",
      "Retener el enlace de la clase en vivo a un alumno moroso",
      "Retener la emisión del certificado a un alumno moroso",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_WRITE_GATES: FeatureManifest = {
  key: "academy.entitlement.write_gates",
  name: "Solo lectura para morosos",
  description:
    "Aplica el modo «solo lectura» a las entregas y al foro. La nota y la asistencia YA obtenidas no se tocan nunca: el expediente académico no se retiene por deuda.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: un feature de dinero que se activa solo es un incidente.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Impedir entregas de tareas a un alumno moroso",
      "Impedir publicar en el foro a un alumno moroso",
    ],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_STUDENT_EXCEPTIONS: FeatureManifest = {
  key: "academy.entitlement.student_exceptions",
  name: "Excepciones por alumno",
  description:
    "Perdonar el bloqueo a un alumno concreto, con motivo y fecha de caducidad OBLIGATORIOS. Solo el dueño o un administrador de la academia pueden concederlas, y cada concesión y cada revocación quedan auditadas.",
  scope: "org",
  status: "beta",
  gateable: true,
  // Nace APAGADA: un feature de dinero que se activa solo es un incidente.
  defaultEnabled: false,
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement.policies"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Conceder y revocar excepciones de titularidad a un alumno"],
    dataPolicy: "preserved",
  },
};

const ENTITLEMENT_SYNC_SCOPING: FeatureManifest = {
  key: "academy.entitlement.sync_scoping",
  name: "Acotar la réplica por titularidad",
  description:
    "Acota qué contenido se replica al dispositivo según la titularidad. NO recorta ningún stream por feature: cambia lo que el servidor escribe en la tabla de cursos con derecho, así que encenderlo o apagarlo no despliega nada y se revierte en un ciclo de recálculo.",
  scope: "platform",
  status: "beta",
  defaultEnabled: false,
  // NO es `gateable`: no gatea ninguna ESCRITURA. Decide qué proyecta el
  // materializador, que es una decisión de proyección y no una barrera —
  // marcarla gateable obligaría a inventarle un `feature_gate` inexistente.
  since: "2026-07",
  docsSlug: "caja",
  requires: ["academy.entitlement"],
  implications: {
    navHidden: [],
    flowsBlocked: ["Retirar del dispositivo del alumno el árbol del curso impagado al reconectar"],
    dataPolicy: "preserved",
  },
};

/** Manifests de la titularidad (fuente `entitlement`). */
export function entitlementManifests(): FeatureManifest[] {
  return [
    ENTITLEMENT,
    ENTITLEMENT_POLICIES,
    ENTITLEMENT_REST_GATES,
    ENTITLEMENT_WRITE_GATES,
    ENTITLEMENT_STUDENT_EXCEPTIONS,
    ENTITLEMENT_SYNC_SCOPING,
  ];
}

/** Keys greppables desde el cliente (espejo de `platform::features::keys`). */
export const FLAG_ENTITLEMENT = ENTITLEMENT.key;
export const FLAG_ENTITLEMENT_POLICIES = ENTITLEMENT_POLICIES.key;
export const FLAG_ENTITLEMENT_REST_GATES = ENTITLEMENT_REST_GATES.key;
export const FLAG_ENTITLEMENT_WRITE_GATES = ENTITLEMENT_WRITE_GATES.key;
export const FLAG_ENTITLEMENT_STUDENT_EXCEPTIONS = ENTITLEMENT_STUDENT_EXCEPTIONS.key;
export const FLAG_ENTITLEMENT_SYNC_SCOPING = ENTITLEMENT_SYNC_SCOPING.key;
